import React, { useState, useEffect } from "react";
import AppLayout from "../../../lib/components/Layout/app-layout";
import Header from "../../../lib/components/Header";
import AppTextField from "../../../lib/components/AppTextField";
import customPlanService from "../../../service/customPlanService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import { ArrowBack, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

const AddBillingPlan = () => {
  const navigate = useNavigate();

  const [periods, setPeriods] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [basicInfo, setBasicInfo] = useState({
    name: "",
    description: "",
  });
  const [form, setForm] = useState({
    cycle: "",
    amount: "",
  });
  const [displayAmount, setDisplayAmount] = useState("");
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [type, setType] = useState("");
  const requiredCycles = ["monthly", "quarterly", "biannually", "annually"];
  const location = useLocation();
  const editingPlan = location.state?.plan || null;
  const isEditMode = !!editingPlan;



  const openAddModal = () => {
    setForm({ cycle: "", amount: "" });
    setDisplayAmount("");
    setEditingIndex(null);
    setOpenModal(true);
  };

  const openEditModal = (index) => {
  const selected = periods[index];
  setForm(selected);
  setDisplayAmount(
    selected.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );
  setEditingIndex(index);
  setOpenModal(true);
  };

  useEffect(() => {
  if (!editingPlan) return;

  // Basic info
  setBasicInfo({
    name: editingPlan.name || "",
    description: editingPlan.description || "",
  });

  setType(editingPlan.type || "");

  const mappedPeriods = editingPlan.pricing
  ? Object.entries(editingPlan.pricing).map(([cycle, value]) => ({
      cycle,
      amount: String(value.amount),
    }))
  : [];

  setPeriods(mappedPeriods);
  }, [editingPlan]);

  const handleSavePeriod = () => {
    if (!form.cycle || !form.amount) return;

    if (editingIndex !== null) {
      // Edit existing
      const updated = [...periods];
      updated[editingIndex] = form;
      setPeriods(updated);
    } else {
      // Add new
      setPeriods([...periods, form]);
    }

    setOpenModal(false);
  };

  const validatePeriods = () => {
  const lowerCycles = periods.map(p => p.cycle.toLowerCase());
  // Check for duplicates
  const uniqueCycles = [...new Set(lowerCycles)];
  if (uniqueCycles.length !== lowerCycles.length) return false;
  // Check all required cycles are included
  for (const cycle of requiredCycles) {
    if (!lowerCycles.includes(cycle)) return false;
  }
  return true;
  };

  const confirmDelete = () => {
    setPeriods(periods.filter((_, idx) => idx !== deleteIndex));
    setDeleteIndex(null);
  };

  const cycleIntervals = {
    monthly: "MONTHLY",
    quarterly: "QUARTERLY",
    biannually: "BIANNUALLY",
    annually: "ANNUALLY",
  };

  const formatNumber = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

    const handleAmountChange = (e) => {
      // Remove non-digit characters
      const rawValue = e.target.value.replace(/\D/g, "");
      setForm({ ...form, amount: rawValue });
      setDisplayAmount(
        rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      );
    };


  const handleSavePlan = async () => {
    if (!validatePeriods()) {
      toast.error("You must add all subscription cycles (Monthly, Quarterly, Biannually, Annually) without duplicates.");
      return;
    }
  const payload = {
    ...(isEditMode && { id: editingPlan.id }),
    name: basicInfo.name,
    type,
    description: basicInfo.description || "",
    periods: periods.map((p) => ({
      cycle: p.cycle.toLowerCase(),
      amount: Number(p.amount),
    })),
  };

  try {
    setLoading(true); 
    let response;
    if (isEditMode) {
      response = await customPlanService.editCustomPlan(payload);
      toast.success("Custom plan updated successfully!");
    } else {
      response = await customPlanService.createCustomPlan(payload);
      toast.success("Custom plan created successfully!");
    }
    await queryClient.invalidateQueries({
      queryKey: ["subscriptionPlans"],
    });
    navigate(-1);
  } catch (error) {
    toast.error(error.response?.data?.message || "Failed to create plan");
  }finally {
    setLoading(false);
  }
};

  // -------------------------
const isSaveDisabled =
  !basicInfo.name.trim() || !type;

  return (
    <AppLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="p-3">
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
      </div>

      <Header
        title={isEditMode ? "Edit Custom Billing Plan" : "Add Custom Billing Plan"}
        description={
          isEditMode
            ? "Update and manage this billing plan."
            : "Create and configure new billing plans here."
        }
      />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
      <div className="bg-white p-4 rounded mb-4">
          {/* Section Title */}
          <h5
            style={{
              fontWeight: 400,
              marginBottom: 24,
            }}
          >
            Basic Information
          </h5>

          {/* Fixed-width form column */}
          <div style={{ maxWidth: 420 }}>
            {/* Plan Name */}
            <div className="mb-3">
              <AppTextField
                label="Plan Name *"
                value={basicInfo.name}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, name: e.target.value })
                }
              />
            </div>

            {/* Plan Type */}
            <div className="mb-3">
              {/* Label */}
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  marginBottom: 6,
                }}
              >
                Plan Type
              </div>

              {/* Select */}
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  sx={{
                    height: 44,
                    borderRadius: "6px",
                    fontSize: 14,
                    backgroundColor: "#FFFFFF",
                    "& .MuiSelect-select": {
                      padding: "10px 12px",
                    },
                    "& fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3366FF",
                      borderWidth: "1px",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <span style={{ color: "#9CA3AF" }}>Select Here</span>
                  </MenuItem>
                  <MenuItem value="CUSTOM">Custom Plan</MenuItem>
                  <MenuItem value="DEFAULT">Default Plan</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Description */}
            <AppTextField
              label="Description (optional)"
              multiline
              minRows={3}
              value={basicInfo.description}
              onChange={(e) =>
                setBasicInfo({ ...basicInfo, description: e.target.value })
              }
            />
          </div>
        </div>

        

        <div className="bg-white p-4 rounded mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 style={{ fontWeight: 400 }}>Manage Billing Periods</h5>

            <Button
              variant="outlined"
              onClick={openAddModal}
              sx={{
                height: 44,
                px: 2.5,
                borderRadius: "6px",
                borderColor: "#3366FF",
                color: "#3366FF",
                backgroundColor: "#EAF0FF",
                textTransform: "none",
                fontWeight: 400,
                fontSize: 12,
                "&:hover": {
                  backgroundColor: "#DDE6FF",
                  borderColor: "#3366FF",
                },
              }}
            >
              Add New&nbsp;+
            </Button>
          </div>

          {/* PERIOD LIST */}
          {periods.map((item, index) => (
            <div
              key={index}
              className="border rounded p-3 mb-3 d-flex justify-content-between align-items-start"
            >
              <div>
                <div className="fw-bold">{item.cycle}</div>
                <div>₦{Number(item.amount).toLocaleString()}</div>
              </div>

              <div>
                <IconButton onClick={() => openEditModal(index)}>
                  <Edit />
                </IconButton>

                <IconButton onClick={() => setDeleteIndex(index)}>
                  <Delete color="error" />
                </IconButton>
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER BUTTONS */}
        <div className="d-flex justify-content-end gap-3">
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={isSaveDisabled}
            style={{
              backgroundColor: "#3366FF",
              color: "#fff",
              textTransform: "none",
            }}
            onClick={handleSavePlan}
          >
            {loading
            ? "Saving..."
            : isEditMode
            ? "Update Plan"
            : "Save Plan"}
          </Button>
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle>
          {editingIndex !== null ? "Edit Period" : "Add Billing Period"}
        </DialogTitle>

        <DialogContent>
          <FormControl fullWidth margin="dense" sx={{ minWidth: 250 }}>
          <InputLabel>Subscription Cycle</InputLabel>
         <Select
          value={form.cycle}
          label="Subscription Cycle"
          onChange={(e) => setForm({ ...form, cycle: e.target.value })}
        >
          {Object.entries(cycleIntervals)
            .filter(([key]) => {
              if (editingIndex !== null) {
                return (
                  key === form.cycle ||
                  !periods.some(p => p.cycle.toLowerCase() === key)
                );
              }

              return !periods.some(p => p.cycle.toLowerCase() === key);
            })
            .map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
        </Select>
        </FormControl>

          <TextField
            fullWidth
            margin="dense"
            type="text"
            label="Amount (₦)"
            value={displayAmount}
            onChange={handleAmountChange}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleSavePeriod}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION */}
      <Dialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)}>
        <DialogTitle>Delete Period?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this billing period?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteIndex(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
};

export default AddBillingPlan;
